import { useDispatch, useSelector } from 'react-redux';
import { format, isValid } from 'date-fns';

import { useQuery, type UseQueryResult } from '@zg-rentals/react-query5';
import {
  getClient,
  RentalManagerUserFreemiumWebApiHandler_charge,
  RentalManagerUserFreemiumWebApiHandler_details,
  RentalManagerUserFreemiumWebApiHandler_startPaymentMethodSession,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

import { RENTAL_MANAGER_API_V1_PREFIX, RENTAL_MANAGER_API_V2_PREFIX } from '../../constants/RouteConstants';

import DispatchConstants from '../../constants/DispatchConstants';
import {
  BEDROOMS_API_CONSTANTS,
  DEFAULT_FILTER_VALUE,
  PROPERTY_TYPE_API_CONSTANTS,
} from '../../constants/PremiumConstants';
import CurrentProperty from '../../model/CurrentProperty';
import ListingActivationUtils from '../../utils/ListingActivationUtils';
import makeRequest from '../../utils/makeRequestUtils';
import PremiumComps from '../../model/PremiumComps';
import PremiumPricingGuidance from '../../model/PremiumPricingGuidance';

import StartPaymentMethodSession from '../../model/StartPaymentMethodSession';
import useRequestConfig from '../../hooks/useRequestConfig';

import type {
  DatarouterError,
  FreemiumChargeResponseWebDto,
  ListingAlias,
  RentalManagerPaymentProductType,
} from '@zg-rentals/rental-manager-env';
import type { StartPaymentMethodSessionProps } from '../../model/StartPaymentMethodSession';

type QueryOptions = {
  onSuccess?: (response: unknown) => void;
  onError?: (err: unknown) => void;
};

type PropertyType = keyof typeof PROPERTY_TYPE_API_CONSTANTS;
type BedroomType = keyof typeof BEDROOMS_API_CONSTANTS;

type UseChargeParams = {
  hostApp?: string;
  listingAlias: ListingAlias;
  productType: RentalManagerPaymentProductType;
};

const useCharge = (queryParams: UseChargeParams): UseQueryResult<FreemiumChargeResponseWebDto, DatarouterError> => {
  const { listingAlias, productType, hostApp } = queryParams;

  const queryFn = () =>
    RentalManagerUserFreemiumWebApiHandler_charge(
      { listingAlias, productType, hostApp },
      getClient()?.extend({ timeout: 5 * 60 * 1000 }),
    );

  const queryHook = useQuery<FreemiumChargeResponseWebDto, DatarouterError>({
    queryKey: ['useCharge', listingAlias],
    queryFn,
    // use refetch to trigger query
    enabled: false,
    staleTime: 0,
    retry: 0,
  });

  return queryHook;
};

const useGetListingInquiries = (
  { propertyType = DEFAULT_FILTER_VALUE, filterType = 'cityState', beds = DEFAULT_FILTER_VALUE, listingId = '' },
  queryOptions = {},
) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();
  // @ts-expect-error
  const { ZRM_USE_FREEMIUM_TEST_DATA } = useSelector((state) => state.app.cookieEnabledServices);

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/freemium/analytics/inquiries`,
      requestConfig,
      config: {
        params: {
          listingId,
          useTestData: ZRM_USE_FREEMIUM_TEST_DATA,
          propertyType: PROPERTY_TYPE_API_CONSTANTS[propertyType as PropertyType],
          filterType,
          bedrooms: BEDROOMS_API_CONSTANTS[beds as BedroomType],
        },
      },
    });

  return useQuery({
    queryKey: ['getListingInquiries', listingId, propertyType, filterType, beds],
    queryFn: async () => {
      const { marketAverage, property, updatedTimestamp } = await queryFn();

      const dataToReturn = {
        marketAverage,
        property,
        dailyCounts: [],
        containsPremiumData: false,
        updatedTimestamp,
      };

      let inquiresInFirst10Days = 0;
      for (let i = 0; i < 10; i++) {
        inquiresInFirst10Days += marketAverage?.dailyCounts[i]?.count;
      }
      dataToReturn.marketAverage.inquiresInFirst10Days = inquiresInFirst10Days;

      marketAverage.dailyCounts.forEach((item: { timestamp?: string; count?: number }, index: number) => {
        const myPropertyData = property?.dailyCounts?.[index];
        const formattedData: { name?: string; average?: number; premium?: boolean; myProperty?: number } = {};

        formattedData.name = isValid(new Date(Number(item?.timestamp)))
          ? format(new Date(Number(item?.timestamp)), 'MM/dd')
          : '';
        formattedData.average = item?.count;
        if (item?.timestamp === myPropertyData?.timestamp) {
          formattedData.premium = myPropertyData?.isPremium;
          formattedData.myProperty = myPropertyData?.count;
        }

        if (formattedData.premium && !dataToReturn.containsPremiumData) {
          dataToReturn.containsPremiumData = true;
        }

        // @ts-expect-error
        dataToReturn.dailyCounts.push(formattedData);
      });

      return dataToReturn;
    },
    ...queryOptions,
    staleTime: 10 * 1000,
  });
};

const useGetListingPrices = (
  { listingId = '', propertyType = DEFAULT_FILTER_VALUE, beds = DEFAULT_FILTER_VALUE, filterType = 'cityState' },
  queryOptions = {},
) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();
  // @ts-expect-error
  const { ZRM_USE_FREEMIUM_TEST_DATA } = useSelector((state) => state.app.cookieEnabledServices);

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V2_PREFIX}/users/freemium/analytics/prices`,
      requestConfig,
      config: {
        params: {
          listingId,
          useTestData: ZRM_USE_FREEMIUM_TEST_DATA,
          propertyType: PROPERTY_TYPE_API_CONSTANTS[propertyType as keyof typeof PROPERTY_TYPE_API_CONSTANTS],
          filterType,
          bedrooms: BEDROOMS_API_CONSTANTS[beds as keyof typeof BEDROOMS_API_CONSTANTS],
        },
      },
    });

  return useQuery({
    queryKey: ['getListingPrices', listingId, propertyType, filterType, beds],
    queryFn: async () => {
      const data = await queryFn();
      // adding a new key (labelListDataKey) that combines a lot of the fields because recharts dataKey only take a string value and this is my hack to pass in a lot of data

      const modifiedPricesArray = data.prices.reduce(
        (
          prevValue: Array<{ [key: string]: string | boolean | number }>,
          currentValue: { isProperty: boolean; count: number; isMarketMedian: boolean; labelListDataKey: string },
        ) => {
          prevValue.push({
            ...currentValue,
            labelListDataKey: `isProperty:${currentValue.isProperty},isMarketMedian:${currentValue.isMarketMedian},count:${currentValue.count}`,
          });
          return prevValue;
        },
        [],
      );

      data.prices = modifiedPricesArray.map((priceData: { [labelListDataKey: string]: string }) => ({
        ...priceData,
        labelListDataKey: `${priceData.labelListDataKey},maxCount:${data.maxCount}`,
      }));
      return data;
    },
    ...queryOptions,
    staleTime: 10 * 1000,
  });
};

const useFreemiumDetails = ({ listingAlias = '' }, queryOptions: QueryOptions = {}) => {
  const dispatch = useDispatch();

  const queryFn = async () => {
    const response = await RentalManagerUserFreemiumWebApiHandler_details({ listingAlias });

    const currentPropertyModel = CurrentProperty({
      freemium: {
        ...response,
      },
    });

    dispatch({
      type: DispatchConstants.UPDATE_CURRENT_PROPERTY,
      payload: currentPropertyModel,
    });

    return response;
  };

  return useQuery({
    queryKey: ['freemiumDetails', listingAlias],
    queryFn,
    ...queryOptions,
    // use refetch to trigger query
    enabled: false,
    staleTime: 10 * 1000,
  });
};

const useGetPremiumOverview = (
  { listingId = '', filterType = 'cityState', beds = DEFAULT_FILTER_VALUE, propertyType = DEFAULT_FILTER_VALUE },
  queryOptions = {},
) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/freemium/analytics/overview`,
      requestConfig,
      config: {
        params: {
          listingId,
          filterType,
          bedrooms: BEDROOMS_API_CONSTANTS[beds as BedroomType],
          propertyType: PROPERTY_TYPE_API_CONSTANTS[propertyType as PropertyType],
        },
      },
    });

  return useQuery({
    queryKey: ['getPremiumOverview', listingId, filterType, beds, propertyType],
    queryFn,
    ...queryOptions,
    staleTime: Infinity,
  });
};

const useGetPremiumComps = (
  { listingId = '', beds = DEFAULT_FILTER_VALUE, filterType = 'cityState', propertyType = DEFAULT_FILTER_VALUE },
  queryOptions = {},
) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();
  // @ts-expect-error
  const { ZRM_USE_FREEMIUM_TEST_DATA } = useSelector((state) => state.app.cookieEnabledServices);

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V2_PREFIX}/users/freemium/analytics/comps`,
      requestConfig,
      config: {
        params: {
          listingId,
          bedrooms: BEDROOMS_API_CONSTANTS[beds as BedroomType],
          useTestData: ZRM_USE_FREEMIUM_TEST_DATA,
          filterType,
          propertyType: PROPERTY_TYPE_API_CONSTANTS[propertyType as PropertyType],
        },
      },
    });

  return useQuery({
    queryKey: ['getPremiumComps', listingId, beds, filterType, propertyType],
    queryFn: async () => {
      const data = await queryFn();
      const premiumComps = PremiumComps(data);
      const formattedGraphData: Array<{ [cityCount: string]: string | null }> = [];

      premiumComps.cityState.dailyCounts.forEach((item) => {
        formattedGraphData.push({
          cityCount: item.count,
          timestamp: format(new Date(Number(parseInt(item.timestamp))), 'M/dd'),
        });
      });

      premiumComps.comps.dailyCounts.forEach((item, index) => {
        formattedGraphData[index].compsCount = item.count;
      });

      return { ...premiumComps, formattedGraphData };
    },
    ...queryOptions,
  });
};

const useStartPaymentMethodSession = (
  { listingAlias, hostApp }: { listingAlias: string; hostApp: string },
  queryOptions: QueryOptions = {},
) => {
  const dispatch = useDispatch();

  const queryFn = () =>
    RentalManagerUserFreemiumWebApiHandler_startPaymentMethodSession(
      { listingAlias, hostApp },
      getClient()?.extend({ timeout: 5000 }),
    );

  return useQuery<StartPaymentMethodSessionProps, DatarouterError>({
    queryKey: ['startPaymentMethodSession', listingAlias],
    queryFn: async () => {
      try {
        const data = await queryFn();
        const sessionData = StartPaymentMethodSession(data);

        queryOptions.onSuccess?.(sessionData);
        dispatch({
          type: DispatchConstants.START_PAYMENT_METHOD_SESSION_SUCCESS,
          payload: sessionData,
        });

        return sessionData;
      } catch (err) {
        queryOptions.onError?.(err);

        if (ListingActivationUtils.isFailedActivationErrorCode(err.code ?? '')) {
          dispatch({
            type: DispatchConstants.LISTING_CANNOT_BE_ACTIVATED,
            payload: err,
          });
        } else {
          dispatch({
            type: DispatchConstants.INITIATE_CREDIT_CARD_CAPTURE_FAIL,
            payload: err,
          });
        }

        throw err;
      }
    },
    ...queryOptions,
    // use refetch to trigger query
    enabled: false,
    staleTime: 0,
  });
};

const useGetPremiumPricingGuidance = ({ listingId = '' }, queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();
  // @ts-expect-error
  const { ZRM_USE_FREEMIUM_TEST_DATA } = useSelector((state) => state.app.cookieEnabledServices);

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/freemium/analytics/pricingGuidance`,
      requestConfig,
      config: {
        params: {
          listingId,
          useTestData: ZRM_USE_FREEMIUM_TEST_DATA,
        },
      },
    });

  return useQuery({
    queryKey: ['getPremiumComps', listingId],
    queryFn: async () => {
      const data = await queryFn();
      return PremiumPricingGuidance(data);
    },
    ...queryOptions,
  });
};

const useGetisTopOfFunnelEnrollmentEligible = (queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();
  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/freemium/isTopOfFunnelEnrollmentEligible`,
      requestConfig,
    });

  return useQuery({
    queryKey: ['getisTopOfFunnelEnrollmentEligible'],
    queryFn: async () => {
      const data = await queryFn();
      return data;
    },
    ...queryOptions,
    gcTime: 60 * 60 * 1000,
    staleTime: 60 * 60 * 1000,
  });
};

export {
  useCharge,
  useFreemiumDetails,
  useGetListingInquiries,
  useGetListingPrices,
  useStartPaymentMethodSession,
  useGetPremiumOverview,
  useGetPremiumComps,
  useGetPremiumPricingGuidance,
  useGetisTopOfFunnelEnrollmentEligible,
};
